<template>
    <div class="slide-cards-container">
        <div v-if="container.class === 'instrument'" class="instrument-summary">
            <h5 class="instrument-id">
                <instrument-logo :type="container.type" />
                {{ instrumentText }} {{ container.esId }}
                <span v-if="container.name">({{ container.name }})</span>
                <b-button
                    id="edit-instrument-name-dropdown-button"
                    variant="none"
                    @click="isEditName = !isEditName"
                >
                    <i class="fa fa-pencil"></i>
                </b-button>
            </h5>
            <slides-count :slides="container.slides" :instrument-type="container.type" />
            &nbsp;
            <span class="slides-label">{{ instrumentSlideLabel }}</span>
            <update-instrument-name-dialog
                v-if="isEditName"
                id="edit-instrument-name-dropdown-content"
                :instrument="container"
                @hide="hideUpdateNameDialog"
                @updateInstrument="updateInstrumentName"
            />
        </div>
        <b-card-group class="slide-card-group">
            <b-card
                v-for="(slide, index) in container.filteredSlides"
                :key="index"
                class="slide-card rounded-sm"
                :class="{ 'slide-exist': slide }"
            >
                <slide-card-content v-if="slide" :slide="slide" :index="index" />
                <slide-card-ghost v-else :index="index" />
            </b-card>
        </b-card-group>
        <div v-if="isPastSlideLoading" class="loading-text">{{ loadingMessage }}</div>
    </div>
</template>

<script>
import SlidesCount from './common/SlidesCount'
import SlideCardContent from './SlideCardContent'
import SlideCardGhost from './SlideCardGhost'
import UpdateInstrumentNameDialog from './common/UpdateInstrumentNameDialog'
import { HOME_PAGE_FINISHED_SLIDE_PAGE_COUNT, INSTRUMENT_TYPE } from '../../business/const'
import InstrumentLogo from './common/InstrumentLogo'
import { mapState } from 'vuex'

export default {
    name: 'SlideCardsContainer',
    components: {
        InstrumentLogo,
        SlidesCount,
        SlideCardContent,
        SlideCardGhost,
        UpdateInstrumentNameDialog
    },
    props: {
        container: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            isEditName: false
        }
    },
    computed: {
        ...mapState({
            isPastSlideLoading: state => state.timelapse.isPastSlideLoading,
            pastSlides: state => state.timelapse.pastSlides,
            pastSlideMetadata: state => state.timelapse.pastSlideMetadata
        }),
        instrumentType() {
            return this.container.type
        },
        instrumentSlideLabel() {
            switch (this.instrumentType) {
                case INSTRUMENT_TYPE.GERI:
                    return this.$translate('chambers_in_use')
                case INSTRUMENT_TYPE.MIRI:
                    return this.$translate('compartments_in_use')
                default:
                    return this.$translate('slides')
            }
        },
        instrumentText() {
            switch (this.instrumentType) {
                case INSTRUMENT_TYPE.GERI:
                case INSTRUMENT_TYPE.MIRI:
                    return null
                default:
                    return this.$translate('instrument')
            }
        },
        loadingMessage() {
            const total = this.pastSlideMetadata.totalCount
            const from = this.pastSlides.length
            const to =
                this.pastSlideMetadata.page === this.pastSlideMetadata.pageCount - 1
                    ? this.pastSlideMetadata.totalCount
                    : this.pastSlides.length + HOME_PAGE_FINISHED_SLIDE_PAGE_COUNT
            return `Loading slide ${from}-${to} out of ${total}`
        }
    },
    methods: {
        updateInstrumentName(name) {
            this.container.name = name
        },
        hideUpdateNameDialog() {
            this.isEditName = false
        }
    }
}
</script>

<style lang="scss" scoped>
.slide-cards-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    @include prevent-select();

    .loading-text {
        color: $light;
    }

    .instrument-summary {
        display: flex;
        align-items: center;
        position: relative;

        .instrument-id {
            padding: 0.5rem 0;
            margin: 0 1.5rem 0 0;
            color: $light-3;
            font-size: 13pt;
            font-weight: 600;
        }
        .slides-count,
        .slides-label {
            color: $light;
        }
    }

    .slide-card-group {
        padding: 1rem 0;
        display: grid;
        column-gap: 1rem;

        @media screen and #{$new-break1}, #{$new-break2} {
            grid-template-columns: 1fr;
            row-gap: 0.5rem;
        }
        @media screen and #{$new-break3} {
            grid-template-columns: repeat(2, 1fr);
            row-gap: 1rem;
        }
        @media screen and #{$new-break4open} {
            grid-template-columns: repeat(3, 1fr);
            row-gap: 1rem;
        }

        .slide-card {
            min-height: $slide-container-height;
            background-color: $dark-3;
            margin-bottom: 0;
            @include prevent-select();

            .card-body {
                width: 100%;
                padding: 0;

                .slide-card-content,
                .slide-card-ghost {
                    height: 100%;
                }
            }
        }
    }
}
</style>
